<template>
  <div class="mt-2 d-flex flex-column align-center">
    <v-chip size="small" class="copy-chip">
      {{ tkey(chipTextTranslationKey) }}
    </v-chip>
    <div class="mt-3 mb-3 d-flex flex-column" :class="selectBorder">
      <v-select
        :model-value="scenarioId || null"
        hide-details
        width="220px"
        max-width="220px"
        :items="scenarios"
        item-title="name"
        item-value="_id"
        persistent-placeholder
        :placeholder="tkey('selectScenario')"
        class="mb-3"
        :class="margin"
        @update:model-value="setScenarioId"
      />
      <v-select
        :model-value="clusterId"
        hide-details
        width="220px"
        max-width="220px"
        :items="clusters"
        item-title="clusterName"
        item-value="clusterId"
        :placeholder="tkey('selectCluster')"
        persistent-placeholder
        class="mb-3"
        :class="margin"
        :disabled="!scenarioId"
        @update:model-value="setClusterId"
      />
      <v-select
        :model-value="storeClassId || null"
        hide-details
        width="220px"
        max-width="220px"
        :items="storeClasses"
        item-title="name"
        item-value="_id"
        :placeholder="tkey('selectStoreClass')"
        persistent-placeholder
        class="mb-3"
        :class="margin"
        :disabled="!scenarioId || clusterId === null"
        @update:model-value="setStoreClassId"
      />
      <v-select
        :model-value="canvasId || null"
        hide-details
        width="220px"
        max-width="220px"
        :items="canvases"
        :item-title="canvasItemTextKey"
        item-value="_id"
        :placeholder="tkey(canvasTranslationKey)"
        persistent-placeholder
        :class="`${margin} ${canvasSelectStyle}`"
        :disabled="canvasSelectDisabled"
        @update:model-value="setCanvasId"
      />
    </div>
  </div>
</template>

<script>
import { useInternationalization } from '@/composables/use-internationalization';
import { filter, isEmpty, keys, map } from 'lodash';

export default {
  props: {
    scenarios: {
      type: Array,
      required: true,
    },
    clustersMap: {
      type: Object,
      required: true,
    },
    furnitureMap: {
      type: Object,
      required: true,
    },
    canvasesMap: {
      type: Object,
      required: true,
    },
    canvasItemTextKey: {
      type: [String, Function],
      required: true,
    },
    chipTextTranslationKey: {
      type: String,
      required: true,
    },
    canvasTranslationKey: {
      type: String,
      required: true,
    },
    left: {
      type: Boolean,
      required: false,
    },
    right: {
      type: Boolean,
      required: false,
    },
    selectedCanvas: {
      type: Object,
      required: false,
      default: () => {},
    },
  },

  setup() {
    const { tkey } = useInternationalization('assortmentCanvasPage.controls');
    return { tkey };
  },

  data() {
    return {
      scenarioId: '',
      clusterId: null,
      clusters: [],
      storeClassId: '',
      storeClasses: [],
      canvasId: '',
      // Use the string 'null' instead of null so Vuetify 3 does not auto-match this item
      // when nothing is selected. JS object key access treats null and 'null' identically,
      // so canvasesMap lookups still work correctly.
      unclustered: { clusterName: this.tkey('noCluster'), clusterId: 'null' },
    };
  },

  computed: {
    canvases() {
      return !this.canvasSelectDisabled &&
        this.canvasesMap[this.scenarioId] &&
        this.canvasesMap[this.scenarioId][this.clusterId]
        ? filter(
            this.canvasesMap[this.scenarioId][this.clusterId],
            x => x.storeClassId === this.storeClassId
          )
        : [];
    },
    canvasSelectDisabled() {
      return !this.scenarioId || !this.storeClassId || this.clusterId === null;
    },
    selectBorder() {
      return this.left ? 'selects-container-left' : 'selects-container-right';
    },
    margin() {
      return this.left ? 'mr-2' : 'ml-2';
    },
    canvasSelectStyle() {
      return this.defaultValuesSet ? 'remove-select-ability' : '';
    },
    defaultValuesSet() {
      return !isEmpty(this.selectedCanvas);
    },
  },

  watch: {
    canvasId: {
      handler() {
        return this.canvasId ? this.$emit('selected', this.canvasId) : this.$emit('unselected', '');
      },
    },
  },

  created() {
    if (this.defaultValuesSet) {
      this.setDefaultValuesToSelectedCanvas();
    }
  },

  methods: {
    setScenarioId(value) {
      if (value === this.scenarioId) return;
      this.clusterId = null;
      this.storeClassId = '';
      this.resetCanvasSelection();
      this.scenarioId = value;
      const allClusters = this.clustersMap[value]
        ? [...this.clustersMap[value], this.unclustered]
        : [this.unclustered];
      // filter to only show clusters that have an associated canvas
      this.clusters = allClusters.filter(x =>
        // String used here to convert null to a string so it can be read from the object key values
        keys(this.canvasesMap[this.scenarioId]).includes(String(x.clusterId))
      );
    },
    setDefaultValuesToSelectedCanvas() {
      const { scenarioId, clusterId, storeClassId, _id } = this.selectedCanvas;
      this.setScenarioId(scenarioId);
      // Map null from server to the string 'null'
      this.setClusterId(clusterId === null ? 'null' : clusterId);
      this.storeClassId = storeClassId;
      this.canvasId = _id;
    },
    setStoreClassId(value) {
      if (value === this.storeClassId) return;
      this.resetCanvasSelection();
      this.storeClassId = value;
      if (this.defaultValuesSet) {
        this.canvasId = this.canvases[0]._id;
      } else {
        this.resetCanvasSelection();
      }
    },
    setClusterId(value) {
      if (value === this.clusterId) return;
      this.storeClassId = '';
      this.clusterId = value;
      this.resetCanvasSelection();
      const allStoreClasses = this.furnitureMap[this.scenarioId];
      this.storeClasses = allStoreClasses.filter(x =>
        map(this.canvasesMap[this.scenarioId][this.clusterId], 'storeClassId').includes(x._id)
      );
    },
    setCanvasId(value) {
      if (value === this.canvasId) return;
      this.canvasId = value;
    },
    resetCanvasSelection() {
      this.canvasId = '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.selects-container-left {
  border-right: 1px solid $assortment-divider-colour;
}
.selects-container-right {
  border-left: 1px solid $assortment-divider-colour;
}

.copy-chip {
  &.theme--light.v-chip {
    background: $assortment-canvas-copy-chip-colour;

    :deep(.v-chip__content) {
      font-weight: bold;
    }
  }
}

.remove-select-ability {
  pointer-events: none;

  :deep(.v-icon) {
    display: none;
  }
  :deep(.v-select__slot) {
    background: white;
  }
}
</style>
